import { Injectable } from '@angular/core';
import { Constants } from '../constant/constants';
import { environment } from '@environments/environment';

declare global {
  interface Window {
    getDigitalGoodsService(serviceName: string): Promise<any>;
  }
}

/**
 * Google Play purchase result
 */
export interface GooglePlayPurchaseResult {
  purchaseToken: string;
  response: PaymentResponse;
}

/**
 * Plan item for Google Play purchase
 */
export interface GooglePlayPlanItem {
  id: string;
  stripePriceId: string;
  googlePlaySku?: string;
  planAmount: number | string;
  planHeaderText?: string;
}

@Injectable({
  providedIn: 'root',
})
export class GooglePlayService {
  private digitalGoodsService: any;
  private supported = false;
  private initialized = false;
  private initPromise: Promise<void> | null = null;

  /** Background warmup state */
  private warmupRunning = false;
  private warmupComplete = false;
  private warmupStartTime = 0;
  private warmupResolvers: Array<() => void> = [];
  /** True while show() is active — prevents warmup probes from creating competing requests. */
  private purchaseInFlight = false;

  constructor() {
    // Lazy initialization - don't init in constructor
  }

  /**
   * Initializes the Google Play Digital Goods Service.
   * Uses a singleton promise to prevent multiple initializations.
   */
  async init(): Promise<void> {
    // Return existing init promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    // Return early if already initialized
    if (this.initialized) {
      return;
    }

    this.initPromise = this.performInit();
    return this.initPromise;
  }

  private async performInit(): Promise<void> {
    if (typeof window === 'undefined' || !('getDigitalGoodsService' in window)) {
      console.log('[GooglePlay] Digital Goods API not available in this environment');
      this.supported = false;
      this.initialized = true;
      return;
    }

    try {
      console.log('[GooglePlay] Calling getDigitalGoodsService...');
      this.digitalGoodsService = await window.getDigitalGoodsService(Constants.GOOGLE_PLAY.BILLING_URL);
      this.supported = !!this.digitalGoodsService;
      console.log('[GooglePlay] getDigitalGoodsService result:', this.supported ? 'connected' : 'null/falsy');
    } catch (error) {
      console.error('[GooglePlay] Billing initialization failed:', error);
      this.supported = false;
    } finally {
      this.initialized = true;
    }
  }

  /**
   * Logs messages only in non-production environments.
   */
  private log(...args: any[]): void {
    if (!environment.production) {
      console.log(...args);
    }
  }

  /**
   * Logs errors (always logged for debugging critical issues).
   */
  private logError(...args: any[]): void {
    console.error(...args);
  }

  /**
   * Checks if Google Play Billing is supported.
   */
  isSupported(): boolean {
    return this.supported;
  }

  /**
   * Ensures the service is initialized before performing operations.
   */
  private async ensureInitialized(): Promise<boolean> {
    if (!this.initialized) {
      await this.init();
    }
    return this.supported && !!this.digitalGoodsService;
  }

  /**
   * Forces re-initialization of the Digital Goods Service.
   * Useful when the Play Store is dormant on ChromeOS to attempt a wake-up.
   */
  private async forceReinitialize(): Promise<void> {
    this.log('[GooglePlay] Forcing re-initialization of Digital Goods Service...');
    this.initialized = false;
    this.initPromise = null;
    await this.init();
  }

  // ─── Background Warmup ────────────────────────────────────────────────

  /**
   * Starts a background warmup loop that repeatedly tries to connect to
   * the Digital Goods Service. On ChromeOS, the Android Runtime (ARC++)
   * can be dormant after a fresh login / cache-clear, causing
   * getDigitalGoodsService() to fail. This warmup actively pokes the
   * TWA billing bridge via canMakePayment() to trigger ARC++ to start.
   *
   * Call this once at app boot (e.g. from AppComponent.ngOnInit).
   * It runs entirely in the background and never throws.
   */
  startBackgroundWarmup(): void {
    // Guard: don't start twice, and skip if already ready
    if (this.warmupRunning || this.warmupComplete) {
      return;
    }
    if (typeof window === 'undefined' || !('getDigitalGoodsService' in window)) {
      console.log('[GooglePlay] Digital Goods API not available — skipping warmup');
      return;
    }

    this.warmupRunning = true;
    this.warmupStartTime = Date.now();
    console.log('[GooglePlay] Starting background warmup...');

    // Fire-and-forget — errors are caught internally
    this.runWarmupLoop().catch(() => {
      /* intentionally swallowed */
    });
  }

  /**
   * Returns a promise that resolves when the billing service is ready,
   * or rejects after `timeoutMs` if it never becomes ready.
   * If the service is already ready, resolves immediately.
   */
  waitUntilReady(timeoutMs = 15000): Promise<void> {
    // Already good to go
    if (this.warmupComplete && this.supported) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      // Register a resolver that the warmup loop will call
      this.warmupResolvers.push(resolve);

      // Set a timeout so callers don't wait forever
      const timer = setTimeout(() => {
        // Remove this resolver
        this.warmupResolvers = this.warmupResolvers.filter(r => r !== resolve);
        if (this.supported && this.warmupComplete) {
          resolve();
        } else {
          reject(new Error('Google Play Billing is still starting up. Please wait a moment and try again.'));
        }
      }, timeoutMs);

      // If already resolved by the time the microtask runs, clear timer
      if (this.warmupComplete && this.supported) {
        clearTimeout(timer);
        resolve();
      }
    });
  }

  /**
   * Internal warmup loop with exponential backoff.
   * Delays: 0s, 3s, 5s, 8s, 12s, 20s  (~48s total)
   */
  private async runWarmupLoop(): Promise<void> {
    const delays = [0, 3000, 5000, 8000, 12000, 20000];
    const maxAttempts = delays.length;

    for (let i = 0; i < maxAttempts; i++) {
      if (this.purchaseInFlight) {
        console.log(`[GooglePlay] Warmup paused — purchase in flight`);
        break;
      }
      if (delays[i] > 0) {
        await this.waitForBillingBridge(delays[i]);
      }

      const elapsed = ((Date.now() - this.warmupStartTime) / 1000).toFixed(1);

      try {
        // Attempt 1: Try getDigitalGoodsService
        this.initialized = false;
        this.initPromise = null;
        await this.init();

        if (this.supported && this.digitalGoodsService) {
          console.log(`[GooglePlay] Warmup attempt ${i + 1}/${maxAttempts}: Billing initialized successfully (took ${elapsed}s)`);
          this.onWarmupSuccess();
          return;
        }
      } catch {
        // init() already handles errors internally, but guard anyway
      }

      // Attempt 2: Send a canMakePayment() wake-up probe to poke ARC++
      console.log(`[GooglePlay] Warmup attempt ${i + 1}/${maxAttempts}: getDigitalGoodsService failed (${elapsed}s), sending wake-up probe...`);
      await this.sendWakeUpProbe();
    }

    // All attempts exhausted
    const elapsed = ((Date.now() - this.warmupStartTime) / 1000).toFixed(1);
    this.logError(
      `[GooglePlay] Warmup failed after ${maxAttempts} attempts (${elapsed}s). Billing may not work until Play Store is opened manually.`,
    );
    this.warmupRunning = false;
    this.warmupComplete = true;
    // Resolve any waiters even on failure — they will check `this.supported`
    this.flushWarmupResolvers();
  }

  /**
   * Called when warmup succeeds. Resolves all pending waiters.
   */
  private onWarmupSuccess(): void {
    this.warmupRunning = false;
    this.warmupComplete = true;
    this.flushWarmupResolvers();
  }

  /**
   * Resolve all pending waitUntilReady() promises.
   */
  private flushWarmupResolvers(): void {
    const resolvers = this.warmupResolvers;
    this.warmupResolvers = [];
    resolvers.forEach(r => r());
  }

  /**
   * Sends a PaymentRequest.canMakePayment() probe to poke the TWA
   * billing bridge, which can trigger ARC++ to start initializing.
   * The result doesn't matter — the side effect of calling it is
   * what wakes up the Android subsystem.
   */
  private async sendWakeUpProbe(): Promise<void> {
    if (this.purchaseInFlight) {
      console.log('[GooglePlay] Skipping wake-up probe — purchase in flight');
      return;
    }
    try {
      const probeRequest = new PaymentRequest([{ supportedMethods: Constants.GOOGLE_PLAY.BILLING_URL, data: { sku: 'probe' } }], {
        total: { label: 'Probe', amount: { currency: 'USD', value: '0.00' } },
      });
      const canPay = await probeRequest.canMakePayment();
      console.log(`[GooglePlay] Wake-up probe result: canMakePayment=${canPay}`);
    } catch (error: any) {
      // Expected to fail when ARC++ is dormant — that's fine
      console.log('[GooglePlay] Wake-up probe threw (expected):', error?.message || error);
    }
  }

  // ─── Billing Bridge Helpers ───────────────────────────────────────────

  /**
   * Waits for a specified delay to allow the TWA billing bridge to initialize.
   */
  private waitForBillingBridge(delayMs = 1000): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, delayMs));
  }

  /**
   * Creates a PaymentRequest and shows it.
   *
   * Key problem on ChromeOS: request.show() can hang indefinitely if the
   * BillingClient connection drops mid-flow. We race show() against a timeout
   * and retry with a fresh connection on timeout.
   *
   * @param paymentMethods Payment method data array
   * @param details Payment details
   * @param maxRetries Total attempts (default 3)
   * @param showTimeoutMs Timeout for request.show() in ms (default 30000)
   */
  private async createAndShowPaymentRequest(
    paymentMethods: PaymentMethodData[],
    details: PaymentDetailsInit,
    maxRetries = 1,
    showTimeoutMs = 45000,
  ): Promise<PaymentResponse> {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      console.log(`[GooglePlay] Attempt ${attempt}/${maxRetries}: creating PaymentRequest...`);

      let request: PaymentRequest;
      try {
        request = new PaymentRequest(paymentMethods, details);
      } catch (error: any) {
        console.error(`[GooglePlay] Failed to construct PaymentRequest:`, error?.name, error?.message);
        throw error;
      }

      // Show the payment sheet immediately while user gesture activation is still valid.
      // Do NOT retry show() in a loop — Chromium Security prohibits multiple show() calls per user gesture!
      console.log(`[GooglePlay] Attempt ${attempt}/${maxRetries}: calling request.show() (timeout: ${showTimeoutMs}ms)...`);
      const showStart = Date.now();
      this.purchaseInFlight = true;
      try {
        const response = await this.showWithTimeout(request!, showTimeoutMs);
        this.purchaseInFlight = false;
        console.log(`[GooglePlay] Attempt ${attempt}: show() resolved in ${Date.now() - showStart}ms`);
        return response;
      } catch (error: any) {
        this.purchaseInFlight = false;
        const elapsed = Date.now() - showStart;
        console.warn(`[GooglePlay] Attempt ${attempt} show() rejected after ${elapsed}ms:`, {
          name: error?.name,
          message: error?.message,
          error,
        });

        if (this.isCancellationError(error, elapsed)) {
          console.log(`[GooglePlay] Attempt ${attempt}: user cancelled after ${elapsed}ms`);
          throw error;
        }
        // ARC++ cold-start: fast rejection with specific AbortError message
        if (elapsed < 1200 && error?.name === 'AbortError') {
          console.log(`[GooglePlay] Cold-start detected after ${elapsed}ms — throwing BILLING_NOT_READY`);
          throw new Error('BILLING_NOT_READY');
        }
        if (error.message === 'PAYMENT_TIMEOUT') {
          console.warn(`[GooglePlay] show() TIMED OUT after ${elapsed}ms`);
          throw new Error('Google Play could not complete the purchase. Please open the Play Store app, wait a few seconds, then try again.');
        }
        throw error;
      }
    }
    throw new Error('Google Play Billing is not available. Please open the Play Store app and try again.');
  }

  /**
   * Races request.show() against a timeout.
   * If the timeout fires first, aborts the payment sheet and throws 'PAYMENT_TIMEOUT'.
   * This is the fix for ChromeOS hanging request.show() when BillingClient disconnects.
   */
  private async showWithTimeout(request: PaymentRequest, timeoutMs: number): Promise<PaymentResponse> {
    let timer: ReturnType<typeof setTimeout> | null = null;

    const timeoutPromise = new Promise<never>((_, reject) => {
      timer = setTimeout(() => {
        reject(new Error('PAYMENT_TIMEOUT'));
      }, timeoutMs);
    });

    try {
      const result = await Promise.race([request.show(), timeoutPromise]);
      if (timer !== null) clearTimeout(timer);
      return result as PaymentResponse;
    } catch (error: any) {
      if (timer !== null) clearTimeout(timer);
      if (error.message === 'PAYMENT_TIMEOUT') {
        // Try to close the hung payment sheet gracefully
        try {
          await request.abort();
          console.log('[GooglePlay] Aborted hung payment sheet');
        } catch {
          console.log('[GooglePlay] request.abort() threw (sheet may already be closed)');
        }
      }
      throw error;
    }
  }

  /**
   * Checks if an error represents a user-initiated cancellation.
   * Different TWA / Chrome OS versions surface this in different ways.
   */
  private isCancellationError(error: any, elapsedMs?: number): boolean {
    if (!error) return false;
    // Fast rejections (<1200ms) with 'invocation was cancelled' or AbortError are cold-start ARC++ wake-ups, not user cancellations
    if (elapsedMs !== undefined && elapsedMs < 1200) {
      const msg = (error.message || '').toLowerCase();
      if (msg.includes('invocation was cancelled') || msg.includes('payment app')) {
        return false;
      }
    }
    if (error.name === 'AbortError') return true;
    const msg = (error.message || '').toLowerCase();
    return (
      msg.includes('cancel') ||
      msg.includes('abort') ||
      msg.includes('dismissed') ||
      msg.includes('user closed') ||
      msg.includes('transient') ||
      msg.includes('user activation') ||
      msg.includes('delegated')
    );
  }

  /**
   * Fetches SKU details from Google Play.
   * @param skus Array of SKU identifiers
   */
  async getDetails(skus: string[]): Promise<any[]> {
    const isReady = await this.ensureInitialized();
    if (!isReady) {
      this.log('[GooglePlay] Cannot fetch details - service not available');
      return [];
    }

    try {
      const details = await this.digitalGoodsService.getDetails(skus);
      this.log('[GooglePlay] SKU details fetched');
      return details;
    } catch (error) {
      this.logError('[GooglePlay] Error fetching SKU details:', error);
      return [];
    }
  }

  /**
   * Initiates a Google Play purchase using Payment Request API.
   * @param sku SKU identifier for the subscription
   * @returns Purchase result with token and response
   */
  async purchase(sku: string): Promise<GooglePlayPurchaseResult> {
    const isReady = await this.ensureInitialized();
    if (!isReady) {
      throw new Error('Google Play Billing not supported on this device');
    }

    // Fetch SKU details to get dynamic pricing information
    const skuDetails = await this.getDetails([sku]);
    if (!skuDetails || skuDetails.length === 0) {
      throw new Error(`Subscription product "${sku}" not found in Google Play. Please try again later.`);
    }

    const itemDetails = skuDetails[0];

    const paymentMethods: PaymentMethodData[] = [
      {
        supportedMethods: Constants.GOOGLE_PLAY.BILLING_URL,
        data: { sku },
      },
    ];

    const details: PaymentDetailsInit = {
      total: {
        label: itemDetails.title || 'Subscription',
        amount: {
          currency: itemDetails.price?.currency || Constants.GOOGLE_PLAY.DEFAULT_CURRENCY,
          value: itemDetails.price?.value || '0.00',
        },
      },
    };

    try {
      const response = await this.createAndShowPaymentRequest(paymentMethods, details);
      const { purchaseToken } = response.details;

      // Guard: if Google Play returns an empty token, treat as user cancellation.
      if (!purchaseToken) {
        this.log('[GooglePlay] purchaseToken is empty — treating as cancelled');
        throw new Error('Purchase was cancelled');
      }

      this.log('[GooglePlay] Purchase successful');
      return { purchaseToken, response };
    } catch (error: any) {
      // Handle user cancellation gracefully
      if (this.isCancellationError(error)) {
        console.log('[GooglePlay] Purchase cancelled by user');
        throw new Error('Purchase was cancelled');
      }
      this.logError('[GooglePlay] Purchase failed:', error);
      throw error;
    }
  }

  /**
   * Initiates a purchase using plan item data (simplified API).
   * @param plan Plan item with id, stripePriceId, and planAmount
   * @returns Purchase result with token and response
   */
  async purchaseWithPlan(plan: GooglePlayPlanItem): Promise<GooglePlayPurchaseResult> {
    const sku = plan.googlePlaySku || plan.stripePriceId;
    if (!sku) {
      throw new Error('Invalid plan: missing sku or stripePriceId');
    }
    return this.purchase(sku);
  }

  /**
   * Creates a simple payment request without SKU lookup (fallback method).
   * @param plan Plan item with pricing info
   * @returns Purchase result
   */
  async purchaseSimple(plan: GooglePlayPlanItem): Promise<GooglePlayPurchaseResult> {
    const paymentMethods: PaymentMethodData[] = [
      {
        supportedMethods: Constants.GOOGLE_PLAY.BILLING_URL,
        data: { sku: plan.googlePlaySku || plan.stripePriceId },
      },
    ];

    const details: PaymentDetailsInit = {
      total: {
        label: plan.planHeaderText || 'Subscription',
        amount: {
          currency: Constants.GOOGLE_PLAY.DEFAULT_CURRENCY,
          value: String(plan.planAmount),
        },
      },
    };

    try {
      const response = await this.createAndShowPaymentRequest(paymentMethods, details);
      const { purchaseToken } = response.details;

      // Guard: if Google Play returns an empty token, treat as user cancellation.
      if (!purchaseToken) {
        this.log('[GooglePlay] purchaseToken is empty — treating as cancelled');
        throw new Error('Purchase was cancelled');
      }

      this.log('[GooglePlay] Simple purchase successful');
      return { purchaseToken, response };
    } catch (error: any) {
      // Let cold-start signal pass through to component
      if (error?.message === 'BILLING_NOT_READY') {
        throw error;
      }
      if (this.isCancellationError(error)) {
        console.log('[GooglePlay] Purchase cancelled by user');
        throw new Error('Purchase was cancelled');
      }
      this.logError('[GooglePlay] Simple purchase failed:', error);
      throw error;
    }
  }

  /**
   * Helper that performs a purchase and acknowledges it after backend confirmation.
   */
  async completePurchase(plan: GooglePlayPlanItem, backendConfirmationFn: (token: string) => Promise<any>): Promise<void> {
    const result = await this.purchaseWithPlan(plan);
    try {
      await backendConfirmationFn(result.purchaseToken);
      // Acknowledgment is now handled by the backend after verification
      // await this.acknowledge(result.purchaseToken);
      await result.response.complete('success');
    } catch (error) {
      await result.response.complete('fail');
      throw error;
    }
  }

  /**
   * Acknowledges a purchase with Google Play.
   * @param purchaseToken Purchase token from successful purchase
   * @param purchaseType Type of purchase
   */
  async acknowledge(purchaseToken: string, purchaseType: 'onetime' | 'subscription' = 'subscription'): Promise<void> {
    const isReady = await this.ensureInitialized();
    if (!isReady) {
      this.log('[GooglePlay] Cannot acknowledge - service not available');
      return;
    }

    try {
      await this.digitalGoodsService.acknowledge(purchaseToken, purchaseType);
      this.log('[GooglePlay] Purchase acknowledged');
    } catch (error) {
      this.logError('[GooglePlay] Error acknowledging purchase:', error);
    }
  }
}
